/**
 * First-touch marketing attribution capture.
 *
 * Persists the first-touch UTM / ad-click params + landing URL/referrer
 * into localStorage so they survive across page loads within the visit.
 * `getAttribution()` also merges the current URL's tracked params as
 * `last_touch` so the backend can see the most recent click too.
 */

const STORAGE_KEY = "overnightly_attribution_v1";

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
] as const;

type TrackedParam = (typeof TRACKED_PARAMS)[number];

export type AttributionData = Partial<Record<TrackedParam, string>> & {
  first_touch_url?: string;
  first_touch_referrer?: string;
  first_touch_at?: string;
};

export type AttributionPayload = AttributionData & {
  last_touch?: Partial<Record<TrackedParam, string>> & { url?: string; at?: string };
};

function readTrackedParams(search: string): Partial<Record<TrackedParam, string>> {
  const out: Partial<Record<TrackedParam, string>> = {};
  try {
    const params = new URLSearchParams(search);
    for (const key of TRACKED_PARAMS) {
      const val = params.get(key);
      if (val) out[key] = val;
    }
  } catch {
    // ignore malformed search string
  }
  return out;
}

function safeStorageGet(): AttributionData | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AttributionData;
  } catch {
    return null;
  }
}

function safeStorageSet(data: AttributionData): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota/private mode errors
  }
}

/**
 * Idempotently record first-touch attribution for this visitor.
 * Safe to call multiple times / on every page load. No-op on the server.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  const existing = safeStorageGet();
  if (existing) return;

  const params = readTrackedParams(window.location.search);
  const hasAny = Object.keys(params).length > 0;
  const referrer = typeof document !== "undefined" ? document.referrer || "" : "";

  // Only record a first touch when there's something worth recording:
  // either a tracked param or a non-empty referrer (external traffic).
  if (!hasAny && !referrer) return;

  const data: AttributionData = {
    ...params,
    first_touch_url: window.location.href,
    first_touch_referrer: referrer || undefined,
    first_touch_at: new Date().toISOString(),
  };

  safeStorageSet(data);
}

/**
 * Return the stored first-touch attribution merged with any tracked
 * params on the current URL as `last_touch`. Returns null if nothing at all.
 */
export function getAttribution(): AttributionPayload | null {
  if (typeof window === "undefined") return null;

  const stored = safeStorageGet();
  const currentParams = readTrackedParams(window.location.search);
  const hasLastTouch = Object.keys(currentParams).length > 0;

  if (!stored && !hasLastTouch) return null;

  const payload: AttributionPayload = { ...(stored || {}) };
  if (hasLastTouch) {
    payload.last_touch = {
      ...currentParams,
      url: window.location.href,
      at: new Date().toISOString(),
    };
  }
  return payload;
}
